import { useEffect, useRef } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../app/appHooks";
import {
  initAnalytics,
  loadCommunityUnits,
  setAnalyticsLeaveTime,
  setSelectedUnit,
} from "./analyticsSlice";
import { getUnitChartData } from "./analyticsThunks";
import { CommunityTypes } from "../../helpers/constants";
import { UnitList } from "./UnitList";
import { AnalyticsViewer } from "./AnalyticsViewer";
import { Prompt } from "react-router-dom";
import { Views } from "../../helpers/constants";
import { DateTimeISO, DateTimeNow } from "../../helpers/datetime";
import { FloatingNotificationPanel } from "../common/FloatingNotificationPanel";
import { AipNotInstalledView } from "./AipNotInstalledView";

import { AnalyticsIcons } from "../../helpers/iconImports";
import { minToolbarHeight } from "../app/App";

const useStyles = makeStyles<Theme, Boolean>((theme: Theme) =>
  createStyles({
    analyticsContainer: {
      display: "flex",
      height: (showBanner) =>
        `calc(100vh - ${minToolbarHeight(showBanner, theme)})`,
    },
    unitListContainer: {
      display: "flex",
      flex: "0 0 300px",
      backgroundColor: theme.palette.light.light,
    },
    analyticsViewerContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      overflowY: "hidden",
      overflowX: "hidden",
      "@media": {
        overflowY: "auto",
      },
      [theme.breakpoints.down("md")]: {
        overflowY: "auto",
      },
    },
    [`@media print`]: {
      unitListContainer: {
        visibility: "hidden",
      },
    },
  })
);

export default function Analytics() {
  const showBanner = useAppSelector((state) => state.headerState.showBanner);

  /* ---- Hooks ---- */
  const classes = useStyles(showBanner);
  const dispatch = useDispatch();

  /* ---- Selectors ---- */
  const lastVisitedTime = useAppSelector(
    (state) => state.analyticsState.lastVisitedTime
  );
  const userUnits = useAppSelector((state) => state.headerState.units);
  const selectedCommunity = useAppSelector(
    (state) => state.headerState.selectedCommunity
  );
  const isAipCommunity =
    selectedCommunity?.community_type === CommunityTypes.aip;
  const isAipNotInstalled =
    isAipCommunity && selectedCommunity?.is_installed === false;
  const analyticsUnits = useAppSelector((state) => state.analyticsState.units);
  const selectedUnit = useAppSelector(
    (state) => state.analyticsState.selectedUnit
  );
  /* ---- Refs ---- */
  const containerRef = useRef<HTMLDivElement | null>(null);

  /* ---- Effects ---- */
  // The header loads the community's units from the API, without units the user can't see
  useEffect(() => {
    if (isAipNotInstalled || !userUnits) return;
    dispatch(loadCommunityUnits(userUnits));
  }, [userUnits, isAipNotInstalled, dispatch]);

  useEffect(() => {
    if (isAipNotInstalled) return;
    if (lastVisitedTime !== undefined) {
      const currentTime = DateTimeNow();
      const lastVisitedDatetime = DateTimeISO(lastVisitedTime);
      const diff = currentTime.diff(lastVisitedDatetime, ["minutes"]);
      if (diff.minutes > 60) {
        dispatch(initAnalytics());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAipNotInstalled]);

  // Auto-select the single unit for AIP communities
  useEffect(() => {
    if (
      isAipCommunity &&
      !isAipNotInstalled &&
      analyticsUnits &&
      analyticsUnits.length > 0 &&
      selectedUnit === undefined
    ) {
      const unitId = analyticsUnits[0].id;
      dispatch(setSelectedUnit(unitId));
      dispatch(getUnitChartData(unitId));
    }
  }, [
    isAipCommunity,
    isAipNotInstalled,
    analyticsUnits,
    selectedUnit,
    dispatch,
  ]);

  const handleNavigateAwayFromAnalytics = () => {
    dispatch(setAnalyticsLeaveTime(DateTimeNow().toISO()));
  };
  useEffect(() => {
    if (isAipNotInstalled) return;
    //preload control images
    type analyticsKeys = keyof typeof AnalyticsIcons;
    Object.keys(AnalyticsIcons).forEach((picture) => {
      const img = new Image();
      img.src = AnalyticsIcons[picture as analyticsKeys];
    });
  }, [isAipNotInstalled]);

  if (isAipNotInstalled) {
    return (
      <div className={classes.analyticsContainer}>
        <AipNotInstalledView />
      </div>
    );
  }

  return (
    <div className={classes.analyticsContainer}>
      {selectedCommunity && !isAipCommunity && (
        <div className={classes.unitListContainer}>
          <UnitList />
        </div>
      )}
      <div
        className={classes.analyticsViewerContainer}
        ref={containerRef}
        id="viewer-container"
      >
        <AnalyticsViewer containerRef={containerRef} />
      </div>
      <FloatingNotificationPanel />
      <Prompt
        message={(location) => {
          if (location.pathname !== Views.ANALYTICS) {
            handleNavigateAwayFromAnalytics();
          }
          return true;
        }}
      />
    </div>
  );
}
